import { supabase } from "./supabase";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

type ApiFetchOptions = RequestInit & { skipImpersonation?: boolean };

// Hard-navigate only when we're not already on the target path. A component
// that re-issues a failing request from the destination page (e.g.
// RumIdentitySync's useMyBusinesses on /no-account) must not re-trigger a
// full-page reload — that is the ADV-1396 infinite-loop.
function redirectOnce(path: string) {
  if (window.location.pathname !== path) {
    window.location.href = path;
  }
}

/**
 * Apply the shared auth/tenant redirects for a non-OK response: sign out +
 * route to /login on 401, /no-account on missing/inactive membership, and
 * /forbidden on a forbidden business — throwing a typed {@link ApiError} for
 * each. Returns normally for any other status so the caller can apply its own
 * handling (apiFetch: the impersonation-confirm retry; both apiFetch and
 * api.download: a generic ApiError). The parsed body is passed in because a
 * Response body can only be read once. Sharing this with api.download is the
 * ADV-1460 fix — expired-session downloads now route to /login instead of
 * surfacing a raw "Download failed: 401".
 */
async function applyAuthRedirects(
  res: Response,
  body: Record<string, string>,
): Promise<void> {
  const errorCode = body.error_code;

  if (res.status === 401) {
    await supabase.auth.signOut();
    redirectOnce("/login");
    throw new ApiError(401, "Session expired");
  }

  if (
    errorCode === "NO_ACCOUNT_MEMBERSHIP" ||
    errorCode === "INACTIVE_ACCOUNT_USER"
  ) {
    redirectOnce("/no-account");
    throw new ApiError(res.status, body.error || "No account membership");
  }

  if (errorCode === "FORBIDDEN_BUSINESS") {
    redirectOnce("/forbidden");
    throw new ApiError(res.status, body.error || "Access denied");
  }
}

async function apiFetch<T>(
  path: string,
  options?: ApiFetchOptions,
): Promise<T> {
  const { skipImpersonation, ...fetchOptions } = options ?? {};

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const isMultipart = fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isMultipart ? {} : { "Content-Type": "application/json" }),
    ...((fetchOptions.headers as Record<string, string>) ?? {}),
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  if (!skipImpersonation) {
    const impersonateId = localStorage.getItem("adva:impersonate-business-id");
    if (impersonateId) {
      headers["x-impersonate-business-id"] = impersonateId;
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const typed = body as Record<string, string>;
    const errorCode = typed.error_code;

    await applyAuthRedirects(res, typed);

    if (errorCode === "IMPERSONATION_CONFIRMATION_REQUIRED") {
      const confirmed = window.confirm(
        "You are about to perform a destructive action while impersonating a business. Are you sure?",
      );
      if (confirmed) {
        return apiFetch<T>(path, {
          ...options,
          headers: {
            ...((options?.headers as Record<string, string>) ?? {}),
            "x-confirm-destructive": "true",
          },
        });
      }
      throw new ApiError(res.status, "Destructive action cancelled");
    }

    throw new ApiError(res.status, typed.error || `API error: ${res.status}`);
  }
  return res.json();
}

// Typed API helpers
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(
    path: string,
    data: unknown,
    opts?: { skipImpersonation?: boolean },
  ) =>
    apiFetch<T>(path, {
      method: "POST",
      body: JSON.stringify(data),
      skipImpersonation: opts?.skipImpersonation,
    }),
  postMultipart: <T>(path: string, form: FormData) =>
    apiFetch<T>(path, {
      method: "POST",
      body: form,
    }),
  patch: <T>(path: string, data: unknown) =>
    apiFetch<T>(path, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  put: <T>(path: string, data: unknown) =>
    apiFetch<T>(path, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  delete: <T>(path: string) =>
    apiFetch<T>(path, {
      method: "DELETE",
    }),
  /**
   * Fetch a file from an authenticated endpoint and return it as a Blob.
   * Use for download flows that can't rely on plain `<a href>` (which
   * cannot attach the Bearer token).
   */
  async download(path: string): Promise<Blob> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }
    const impersonateId = localStorage.getItem("adva:impersonate-business-id");
    if (impersonateId) {
      headers["x-impersonate-business-id"] = impersonateId;
    }
    const res = await fetch(`${API_BASE}${path}`, { headers });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<
        string,
        string
      >;
      // Share apiFetch's 401 → signOut()+redirect (and no-account/forbidden)
      // handling so an expired-session download routes to /login rather than
      // throwing a raw "Download failed: 401" (ADV-1460).
      await applyAuthRedirects(res, body);
      throw new ApiError(
        res.status,
        body.error || `Download failed: ${res.status}`,
      );
    }
    return res.blob();
  },
};
